import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import config from '@configFile';
import { useGetUserData } from '@hooks/useGetUserData';
import { DealPostType } from '@types';
import MonetizingLink from '@components/MonetizingLink';
import { CarouselRibbon } from '@components/Ribbon';
import AdminActions from '@components/AdminActions';
import ReportInaccurate from '@components/ReportInaccurate';
import ShareButton from '@components/ShareButton';
import FavoriteButton from '@components/FavoriteButton';
import PosterDetails from '@components/PosterDetails';

import {
  getCurrentDealEventStrObj,
  parseTitle,
  getPostPrice,
  truncateTitle,
  getProductLink,
  getPostPrefix
} from '@utils/index';

import { getScaledImage } from '@utils/imageUtils';
import CouponButton from '@components/CouponButton';
import { getCommonBackground } from '@pages/Blog/utils';
import Title from '../Title';
import PostEndFeatures from '../PostEndFeatures';
import SignupForm from '../SignUpForm';
import DealPostFeedCardTabs from './DealPostFeedCardTabs';

interface DealPostFeedCardProps {
  deal: DealPostType;
  relatedDeals?: DealPostType[];
  permalink: string;
  postDate?: string;
  isMainContent?: boolean;
  isFirstContent?: boolean;
}

const DealPostFeedCard = ({
  deal,
  permalink,
  postDate,
  isMainContent = false,
  isFirstContent = false,
  relatedDeals = []
}: DealPostFeedCardProps) => {
  const imageTargetHeight = 300;
  const imageTargetSmallHeight = 240;
  const displayFullRatio = 3;
  const theme = useTheme();

  const tag = isMainContent
    ? config.AFFILIATE_TAGS.FEED_MAIN
    : config.AFFILIATE_TAGS.FEED;
  const { data: user } = useGetUserData();

  const isAdmin = user?.isAdmin || false;
  const { ASIN, finalPrice, isExpired, promoCode } = deal;

  const title = useMemo(
    () => truncateTitle(parseTitle(deal.title), 150),
    [deal.title]
  );
  const link = useMemo(
    () => `${config.CURRENT_URL}/blog/${permalink}`,
    [permalink]
  );

  const postPrice = useMemo(() => getPostPrice(deal), [deal]);
  const scaledImage = useMemo(
    () =>
      getScaledImage(
        deal.image600Width,
        deal.image600Height,
        imageTargetHeight
      ),
    [deal.image600Width, deal.image600Height]
  );

  const smallScaledImage = useMemo(
    () =>
      getScaledImage(
        deal.image240Width,
        deal.image240Height,
        imageTargetSmallHeight
      ),
    [deal.image240Width, deal.image240Height]
  );

  const useScaledImage = useMemo(() => {
    return scaledImage.width / scaledImage.height < displayFullRatio;
  }, [scaledImage.width, scaledImage.height, imageTargetHeight]);

  const useSmallScaledImage = useMemo(() => {
    return smallScaledImage.width / smallScaledImage.height < displayFullRatio;
  }, [smallScaledImage.width, smallScaledImage.height, imageTargetSmallHeight]);

  const imageWebP = deal.image600Url?.replace('.jpg', '.webp');
  const smallImageWebP = deal.image240Url?.replace('.jpg', '.webp');
  const productLink = getProductLink(ASIN, tag, deal.sLink || '');

  const renderActions = () => (
    <Box display="flex" justifyContent="flex-end">
      {promoCode && (
        <CouponButton
          ASIN={ASIN}
          promoCode={promoCode}
          tag={tag}
          size="medium"
        />
      )}
      <ShareButton
        title={`${parseTitle(title)}${postPrice}`}
        url={`${config.SITE_URL}/blog/${permalink}`}
      />
      <ReportInaccurate ASIN={ASIN} />
      <FavoriteButton ASIN={ASIN} finalPrice={finalPrice} />
      {isAdmin && <AdminActions ASIN={ASIN} deal={deal} />}
    </Box>
  );

  const renderRibbonOrSponsored = () => {
    if (deal?.s) {
      return (
        <Typography
          variant="caption"
          sx={{ color: '#767676', position: 'absolute', fontStyle: 'italic' }}
        >
          Sponsored
        </Typography>
      );
    }
    return (
      <Box position="relative" marginLeft="-17px">
        <Box display="flex" justifyContent="flex-start">
          <CarouselRibbon dealSummary={deal} left="1px" opacity={0.85} />
        </Box>
      </Box>
    );
  };

  const shouldShowPostDate = useMemo(() => {
    if (isExpired) return false;
    // if (isMainContent) {
    //   const postTime = new Date(deal?.postDate || '');
    //   const diffHours = Math.ceil(
    //     (Date.now() - postTime.getTime()) / (1000 * 60 * 60)
    //   );
    //   return diffHours < 6;
    // }
    return true;
  }, [isExpired]);
  // }, [deal.postDate, isExpired, isMainContent]);

  const useRawDimensions =
    deal.image600Width &&
    deal.image600Height &&
    deal.image600Height < imageTargetHeight;

  const rawStyles = useRawDimensions
    ? {
        maxWidth: 'calc(100% - 40px)'
      }
    : {
        width: 'calc(100% - 40px)',
        height: '200px',
        objectFit: 'contain'
      };

  const event = getCurrentDealEventStrObj();
  const prefix = React.useMemo(() => {
    return !!event?.showSitePrefix &&
      deal?.listingMode.toLowerCase().includes('prime')
      ? getPostPrefix()
      : '';
  }, [event, deal?.listingMode]);

  const isDarkMode = theme.palette.mode === 'dark';

  const additionalImageStyles: React.CSSProperties = isDarkMode
    ? {
        opacity: 0.9
      }
    : {};

  return (
    <Box
      sx={{
        margin: '0px auto 0 auto'
      }}
      id={deal.slug}
      component="article"
    >
      <Title
        link={link}
        isMainContent={!!isMainContent}
        permalink={permalink}
        title={
          <>
            {`${prefix}${title} `}
            <Box component="span" sx={{ fontWeight: 500 }}>
              {postPrice}
            </Box>
          </>
        }
      />
      <Typography variant="caption" component="div">
        <Box
          sx={getCommonBackground(
            theme.palette.secondaryBackground,
            isDarkMode
          )}
        >
          <PosterDetails postDate={shouldShowPostDate ? postDate : undefined} />
        </Box>
      </Typography>
      {renderRibbonOrSponsored()}
      <Box
        position="absolute"
        right="-10px"
        sx={{
          backgroundColor: theme.palette.actionsBackgroundColor,
          borderRadius: '3px'
        }}
      >
        {renderActions()}
      </Box>

      {deal.isExpired ? <SignupForm /> : null}
      <MonetizingLink
        href={productLink}
        target="_blank"
        sx={{
          display: 'block',
          margin: '20px auto 0 auto',
          textAlign: 'center'
        }}
        clickType={productLink}
        item="blog-deal-image"
        tag={tag}
      >
        <Box
          sx={{
            display: {
              xs: 'none',
              sm: 'block'
            }
          }}
        >
          <Box
            component="img"
            src={(imageWebP || deal.image)?.replace('http:', 'https:')}
            alt={title}
            loading={isFirstContent ? 'eager' : 'lazy'}
            sx={{
              ...(useScaledImage
                ? {
                    objectFit: 'contain'
                  }
                : rawStyles),
              ...additionalImageStyles
            }}
            width={useScaledImage ? scaledImage.width : undefined}
            height={useScaledImage ? scaledImage.height : undefined}
            style={{
              maxWidth: useScaledImage ? '100%' : 'auto'
            }}
          />
        </Box>
        <Box
          sx={{
            display: {
              xs: 'block',
              sm: 'none'
            }
          }}
        >
          <Box
            component="img"
            src={(smallImageWebP || deal.image)?.replace('http:', 'https:')}
            alt={title}
            loading={isFirstContent ? 'eager' : 'lazy'}
            sx={
              useSmallScaledImage
                ? {
                    objectFit: 'contain'
                  }
                : rawStyles
            }
            width={useSmallScaledImage ? smallScaledImage.width : undefined}
            height={useSmallScaledImage ? smallScaledImage.height : undefined}
            style={{
              maxWidth: useSmallScaledImage ? '100%' : 'auto'
            }}
          />
        </Box>
      </MonetizingLink>
      <DealPostFeedCardTabs
        deal={deal}
        relatedDeals={relatedDeals}
        tag={tag}
        showAdditionalInfo={isMainContent || isFirstContent}
        isMainContent={isMainContent}
      />
      <PostEndFeatures />
    </Box>
  );
};

export default React.memo(DealPostFeedCard);
